/**
 * Domeniul propriu al restaurantului.
 *
 * Disponibil doar când planul include `custom_domain` (Pro și Business). Pachetul gratuit
 * nu vede pagina în meniu, iar apelurile spre server sunt respinse la fel.
 */
import { useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Badge,
  Button,
  Card,
  Field,
  InlineAlert,
  Input,
  PageHeader,
  Select,
  Skeleton,
} from '../components/ui.js';
import { api } from '../lib/api.js';
import { useResource } from '../lib/hooks.js';
import { errorMessage, fieldErrors, useSession } from '../lib/session.js';
import { useToast } from '../components/ui.js';

interface Restaurant {
  id: string;
  name: string;
}

interface DomainRecord {
  id: string;
  hostname: string;
  is_primary: boolean;
  verification_token: string;
  verified_at: string | null;
  ssl_status: string;
}

export function DomainPage() {
  const session = useSession();
  const toast = useToast();
  const allowed = session.features.includes('custom_domain');
  const restaurants = useResource<Restaurant[]>(() => api.get<Restaurant[]>('/restaurants'), [session.activeAccountId]);
  const [restaurantId, setRestaurantId] = useState('');
  const selected = restaurantId || restaurants.data?.[0]?.id || '';
  const domains = useResource<DomainRecord[]>(
    () => (allowed && selected ? api.get<DomainRecord[]>(`/restaurants/${selected}/domains`) : Promise.resolve([])),
    [allowed, selected, session.activeAccountId],
  );
  const [hostname, setHostname] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldError, setFieldError] = useState<string[]>([]);

  async function add() {
    if (!selected) return;
    setBusy(true);
    setError(null);
    setFieldError([]);
    try {
      await api.post(`/restaurants/${selected}/domains`, { hostname: hostname.trim().toLowerCase() });
      setHostname('');
      toast.success('Domeniu adăugat', 'Creează înregistrarea TXT, apoi apasă Verifică.');
      await domains.reload();
    } catch (err) {
      setFieldError(fieldErrors(err).hostname ?? []);
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function verify(domain: DomainRecord) {
    setBusy(true);
    try {
      await api.post(`/restaurants/${selected}/domains/${domain.id}/verify`);
      toast.success('Domeniul a fost verificat', `${domain.hostname} este confirmat.`);
      await domains.reload();
    } catch (err) {
      toast.error('Domeniul nu a putut fi verificat', fieldErrors(err).hostname?.[0] ?? errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  async function remove(domain: DomainRecord) {
    setBusy(true);
    try {
      await api.del(`/restaurants/${selected}/domains/${domain.id}`);
      toast.success('Domeniul a fost scos');
      await domains.reload();
    } catch (err) {
      toast.error('Domeniul nu a putut fi scos', errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  if (!allowed) {
    return (
      <div className="page-enter">
        <PageHeader title="Domeniu" subtitle="Meniul public pe adresa restaurantului." />
        <Card className="flex flex-col gap-3">
          <h2 className="font-display text-title text-ink-950">Disponibil pe Pro și Business</h2>
          <p className="text-body-s text-ink-600">
            Pachetul gratuit folosește adresa Menuvia. Domeniul propriu, de forma meniu.restaurantul.ro, se activează odată cu un pachet mai mare.
          </p>
          <Link to="/app/billing" className="text-body-s font-medium text-brand-700">
            Vezi abonamentul
          </Link>
        </Card>
      </div>
    );
  }

  return (
    <div className="page-enter">
      <PageHeader
        title="Domeniu"
        subtitle="Meniu public pe adresa restaurantului. Inclus în pachetele Pro și Business."
      />

      <Card className="mb-4 flex flex-col gap-4">
        <Field label="Restaurant">
          {(props) => (
            <Select {...props} value={selected} onChange={(event) => setRestaurantId(event.target.value)}>
              {(restaurants.data ?? []).map((restaurant) => (
                <option key={restaurant.id} value={restaurant.id}>
                  {restaurant.name}
                </option>
              ))}
            </Select>
          )}
        </Field>
        <Field label="Domeniu" hint="Fără https. Exemplu: meniu.restaurantul.ro" errors={fieldError}>
          {(props) => (
            <Input
              {...props}
              value={hostname}
              onChange={(event) => setHostname(event.target.value)}
              placeholder="meniu.restaurantul.ro"
            />
          )}
        </Field>
        {error ? <InlineAlert tone="danger">{error}</InlineAlert> : null}
        <div>
          <Button loading={busy} disabled={hostname.trim().length < 4 || !selected} onClick={() => void add()}>
            Adaugă domeniul
          </Button>
        </div>
      </Card>

      {domains.loading ? (
        <Skeleton className="h-24" />
      ) : domains.error ? (
        <InlineAlert tone="warning">{domains.error}</InlineAlert>
      ) : (
        <ul className="flex flex-col gap-3">
          {(domains.data ?? []).map((domain) => (
            <Card as="li" key={domain.id} className="flex flex-col gap-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <h2 className="font-medium text-ink-950">{domain.hostname}</h2>
                  <p className="text-caption text-ink-400">
                    {domain.is_primary ? 'Principal · ' : ''}
                    {domain.verified_at ? 'Verificat' : 'Așteaptă înregistrarea TXT'}
                  </p>
                </div>
                <Badge tone={domain.verified_at ? 'success' : 'warning'}>
                  {domain.verified_at ? domain.ssl_status : 'neverificat'}
                </Badge>
              </div>
              {domain.verified_at ? null : (
                <div className="rounded-[var(--radius-sm)] bg-surface-3/70 p-3 text-caption text-ink-700">
                  <p>Tip: TXT</p>
                  <p className="mt-1 font-mono">Nume: _menuvia.{domain.hostname}</p>
                  <p className="mt-1 break-all font-mono">Valoare: {domain.verification_token}</p>
                  <p className="mt-2">După verificare, îndreaptă domeniul cu CNAME către meniu.listara.online.</p>
                </div>
              )}
              <div className="flex flex-wrap gap-2">
                {domain.verified_at ? null : (
                  <Button size="sm" variant="secondary" loading={busy} onClick={() => void verify(domain)}>
                    Verifică
                  </Button>
                )}
                <Button size="sm" variant="ghost" className="text-danger" onClick={() => void remove(domain)}>
                  Șterge
                </Button>
              </div>
            </Card>
          ))}
          {(domains.data ?? []).length === 0 ? (
            <p className="text-body-s text-ink-500">Niciun domeniu configurat pentru restaurantul ales.</p>
          ) : null}
        </ul>
      )}
    </div>
  );
}
